/**
 * EventPage — the operational wing of the institution.
 *
 * Mode 2 (Functional Editorial). Per Craig's lock (2026-05-13):
 *   • graphite/charcoal foundations — never walnut
 *   • restrained gold accent (the only colour)
 *   • parchment-edge panels, hairline dividers, editorial typography
 *   • four editorial stewardship tabs: Setup · Wall · Archive · Recap
 *   • QuietPress tactile cadence, no hover-lift, no glow expansion
 *   • banned vocabulary: Upgrade, Premium, Unlock, Engagement, Growth,
 *     Activate, Boost
 *
 * What this is NOT:
 *   • a SaaS dashboard with metric tiles
 *   • a launcher-stack of utility cards
 *   • a startup admin screen with cyan accents
 *
 * What this IS:
 *   • a composed register the host steps INTO after opening a
 *     preserved night — the curator's stewardship room
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { brandToast } from '../lib/brandToast';

import {
  MODE_TWO,
  PageShell, PageGutter,
  Eyebrow, EditorialHeading, RestrainedMeta,
  GraphitePanel, MutedDivider,
  QuietPress,
  deriveEventState, EVENT_STATES,
  StateGlyph, StateLabel,
} from '../components/ModeTwoPrimitives';
import { EventCompanionsModeTwo, isLeaversEdition } from '../components/EventCompanions';

import FilmModule from '../components/FilmModule';
import GuestAccessSetup from './events/GuestAccessSetup';
import FeatureEventToggle from '../components/FeatureEventToggle';
import HighlightModule from '../components/HighlightModule';
import PromHighlightBanner from '../components/PromHighlightBanner';
import Year6TeamPicker from '../components/Year6TeamPicker';
import SlideshowModule from '../components/SlideshowModule';
import ShareKit from '../components/ShareKit';
import MemoryBookModule from '../components/MemoryBookModule';
import MemoryWallPreviewTile from '../components/MemoryWallPreviewTile';
import DropOneWordHostWidget from '../components/DropOneWordHostWidget';
import LiveMirrorPreview from '../components/LiveMirrorPreview';
import MemoryChaptersModal from '../components/MemoryChaptersModal';
import EventPosterPill from '../components/EventPosterPill';
import MemoryRecapPill from '../components/MemoryRecapPill';
import Bookshelf from '../components/Bookshelf';
import KioskQrModal from '../components/KioskQrModal';
import KioskStatusPill from '../components/KioskStatusPill';
import PaywallModal from '../components/PaywallModal';
import QRDesignEditor from '../components/QRDesignEditor';
import TonightTab from '../components/TonightTab';
import { getMediaUrl } from '../lib/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tabs the host moves between — editorial stewardship, NOT feature
// pills. Keys are stable; labels carry the restrained register voice.
//
// "tonight" is a state-conditional fifth tab that surfaces only
// while the night is *living* — i.e. when deriveEventState returns
// 'gathering' or 'returning'. The label ends in a ✦ to signal the
// register switch (Mode 1 / atmospheric) inside an otherwise Mode 2
// frame. Deep-links never select tonight; it is an offering, not a
// destination.
const BASE_TABS = [
  { id: 'setup',   label: 'Setup'   },
  { id: 'wall',    label: 'Wall'    },
  { id: 'archive', label: 'Archive' },
  { id: 'recap',   label: 'Recap'   },
];
// Booth Sessions (EventDrop Pro ✦) — no planning layer, so the
// Recap tab is intentionally absent. Setup / Wall / Archive only.
// Tonight ✦ still slips in when the session is alive.
const PRO_SESSION_TABS = [
  { id: 'setup',   label: 'Setup'   },
  { id: 'wall',    label: 'Wall'    },
  { id: 'archive', label: 'Archive' },
];
const TONIGHT_TAB = { id: 'tonight', label: 'Tonight ✦' };
const TONIGHT_VISIBLE_STATES = new Set(['gathering', 'returning']);

function buildTabs(eventState, event) {
  const base = event?.event_type === 'booth_session' ? PRO_SESSION_TABS : BASE_TABS;
  // Tonight ✦ slips in between Setup and Wall when the night is
  // alive. Order is intentional: it sits beside Setup (where the
  // host minted the booth pairing) and just before Wall (where the
  // memories are landing) so the eye travels the right way.
  if (TONIGHT_VISIBLE_STATES.has(eventState)) {
    return [base[0], TONIGHT_TAB, ...base.slice(1)];
  }
  return base;
}

// Legacy alias — the few spots that already use TABS for validation
// continue to work; deep-link allowance is enforced separately.
const TABS = BASE_TABS;

// Deep-link payloads from the dashboard / emails map onto a tab so
// the host lands inside the right room. No scroll-by-id any more —
// the room IS the focus. iter180 — accept both underscore and
// hyphen variants for forgiving compatibility with already-shipped
// email links.
const FOCUS_TO_TAB = {
  film: 'recap', highlight: 'recap', pro: 'recap',
  guestbook: 'wall',
  memory_book: 'archive', 'memory-book': 'archive',
  booth: 'wall', pocket_booth: 'wall', 'pocket-booth': 'wall',
  challenges: 'wall', invites: 'setup',
  branding: 'setup', qr: 'setup', share: 'setup',
  // EventDrop Pro ✦ deep-link — `Pair Booth ✦` in the studio
  // appends ?focus=pairing so the operator's eye lands directly on
  // the Booth OS pairing code without scrolling the Setup tab.
  pairing: 'setup',
};

function formatEventDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
}


// ─────────────────────────────────────────────────────────────────
//  TabBar — editorial stewardship tabs, restrained gold underline
// ─────────────────────────────────────────────────────────────────
const TabBar = ({ tab, setTab, tabs = TABS }) => (
  <div
    data-testid="event-tabs"
    role="tablist"
    style={{
      display: 'flex', gap: 0, alignItems: 'stretch',
      borderBottom: `1px solid ${MODE_TWO.hairSoft}`,
      marginBottom: 'clamp(24px, 3vw, 36px)',
      overflowX: 'auto',
    }}
  >
    {tabs.map((t) => {
      const active = tab === t.id;
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => setTab(t.id)}
          data-testid={`event-tab-${t.id}`}
          data-active={active ? '1' : '0'}
          style={{
            position: 'relative',
            background: 'transparent',
            border: 'none',
            padding: '14px 22px 16px',
            cursor: 'pointer',
            fontFamily: MODE_TWO.serif,
            fontStyle: 'italic',
            fontSize: 14.5,
            letterSpacing: '0.02em',
            color: active ? MODE_TWO.ink : MODE_TWO.inkMuted,
            transition: 'color 220ms ease',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{t.label}</span>
          {active && (
            <span style={{
              position: 'absolute', left: 22, right: 22, bottom: -1,
              height: 1, background: MODE_TWO.gold,
            }} />
          )}
        </button>
      );
    })}
  </div>
);


// ─────────────────────────────────────────────────────────────────
//  Section atom — a quiet editorial header above a content block
// ─────────────────────────────────────────────────────────────────
const Section = ({ eyebrow, title, sub, children, testId, action }) => (
  <section data-testid={testId} style={{ marginBottom: 'clamp(28px, 3.5vw, 44px)' }}>
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-end', gap: 16, flexWrap: 'wrap',
      marginBottom: 14,
    }}>
      <div>
        {eyebrow && <Eyebrow>✦ {eyebrow}</Eyebrow>}
        {title && <div style={{ marginTop: 8 }}><EditorialHeading size="h2">{title}</EditorialHeading></div>}
        {sub && <div style={{
          marginTop: 8, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMuted, lineHeight: 1.6,
          maxWidth: 620,
        }}>{sub}</div>}
      </div>
      {action && <div>{action}</div>}
    </div>
    <MutedDivider tone={MODE_TWO.hairSoft} />
    <div style={{ marginTop: 8 }}>{children}</div>
  </section>
);


// ─────────────────────────────────────────────────────────────────
//  EventHeader — name, date, state glyph, photo count line
// ─────────────────────────────────────────────────────────────────
const EventHeader = ({ event, photoCount, onBack }) => {
  const state = deriveEventState(event);
  const tier = (event.package_type || 'standard').toUpperCase();
  return (
    <div style={{ marginBottom: 18 }}>
      <button
        type="button"
        onClick={onBack}
        data-testid="back-to-register-link"
        style={{
          background: 'none', border: 'none', padding: 0,
          color: MODE_TWO.inkMuted,
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13, letterSpacing: '0.04em',
          cursor: 'pointer', textDecoration: 'none',
        }}
      >← All events</button>

      <div style={{ marginTop: 22 }}>
        <Eyebrow>✦ Event dashboard</Eyebrow>
      </div>

      <div style={{ marginTop: 10 }}>
        <EditorialHeading size="h1">
          <span data-testid="event-title">{event.event_name}</span>
        </EditorialHeading>
      </div>

      <div style={{
        marginTop: 14, display: 'flex', flexWrap: 'wrap',
        alignItems: 'center', gap: '8px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StateGlyph state={state} />
          <StateLabel state={state} />
        </div>
        <span style={{ color: MODE_TWO.hair }}>·</span>
        <span data-testid="event-date" style={{
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMid,
        }}>{formatEventDate(event.event_date)}</span>
        <span style={{ color: MODE_TWO.hair }}>·</span>
        <RestrainedMeta testId="photo-count">
          {photoCount} {photoCount === 1 ? 'upload' : 'uploads'}
        </RestrainedMeta>
        <span style={{ color: MODE_TWO.hair }}>·</span>
        <RestrainedMeta testId="event-tier">{tier}</RestrainedMeta>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────
//  PaymentNote — restrained operational banner (Mode 2)
// ─────────────────────────────────────────────────────────────────
const PaymentNote = ({ status, paymentStatus, celebrationShown }) => {
  if (paymentStatus === 'polling') {
    return (
      <GraphitePanel testId="payment-polling-banner" padding="14px 18px" style={{ marginBottom: 22 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMid,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: MODE_TWO.goldSoft,
            animation: 'mode2Breathe 2.4s ease-in-out infinite',
          }} />
          Confirming the commission…
        </div>
      </GraphitePanel>
    );
  }
  if (paymentStatus === 'paid' || celebrationShown) {
    return (
      <GraphitePanel testId="payment-success-banner" padding="14px 18px" style={{ marginBottom: 22 }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 12,
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMid,
        }}>
          <span style={{ color: MODE_TWO.gold }}>✦</span>
          The night is settling in.
          <span style={{ color: MODE_TWO.inkFaint, fontStyle: 'normal', fontSize: 12 }}>
            The QR plate is ready to share.
          </span>
        </div>
      </GraphitePanel>
    );
  }
  if (status === 'pending' && paymentStatus !== 'polling' && paymentStatus !== 'paid' && !celebrationShown) {
    return (
      <GraphitePanel testId="payment-pending-banner" padding="14px 18px" style={{ marginBottom: 22 }}>
        <div style={{
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMid,
        }}>
          The commission is preparing — complete it to begin the night.
        </div>
      </GraphitePanel>
    );
  }
  return null;
};


// ─────────────────────────────────────────────────────────────────
//  LeaveYourMarkSection — Remember Us ✦ emotional centrepiece
//  (iter195, Leavers Night only)
//
//  The shared wall is the participation substrate for schools.
//  This section visually outranks Drop One Word and the
//  companions grid surrounding it: gold rule, larger eyebrow,
//  generous interior padding, an embedded live projector preview
//  beneath the copy + CTAs. Per Belief 2 & 5 — the year group
//  writes itself; this is where that writing happens.
// ─────────────────────────────────────────────────────────────────
const LeaveYourMarkSection = ({ event, slug }) => {
  const openRemember = () => window.open(`${window.location.origin}/remember-us/${slug}`, '_blank', 'noopener');
  const openProjector = () => window.open(`${window.location.origin}/live/remember-us/${slug}`, '_blank', 'noopener');
  return (
    <section
      data-testid="setup-leave-your-mark-section"
      style={{ marginTop: 'clamp(40px, 5vh, 64px)' }}
    >
      <GraphitePanel padding="clamp(28px, 4vw, 44px)">
        <div style={{
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: MODE_TWO.gold,
        }} data-testid="leave-your-mark-eyebrow">
          ✦ Leave your mark ✦
        </div>
        <div style={{ marginTop: 14 }} data-testid="leave-your-mark-title">
          <EditorialHeading size="h2">
            The room slowly remembers itself <span style={{ color: MODE_TWO.gold }}>✦</span>
          </EditorialHeading>
        </div>
        <p
          data-testid="leave-your-mark-body"
          style={{
            margin: '18px 0 0', maxWidth: 620,
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 15, lineHeight: 1.7, color: MODE_TWO.inkMid,
          }}
        >
          Messages, drawings, signatures, jokes and fragments appear live
          throughout the evening — building a shared wall the year group
          leaves behind.
        </p>

        <div
          data-testid="leave-your-mark-actions"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 28 }}
        >
          <QuietPress onClick={openRemember} testId="leave-your-mark-open-remember">
            Open Remember Us ✦
          </QuietPress>
          <QuietPress variant="quiet" onClick={openProjector} testId="leave-your-mark-open-projector">
            Open projector wall ✦
          </QuietPress>
        </div>

        {/* Live preview — chrome-free projector embedded as a small
            quiet window. The /live/remember-us/:slug surface is
            intentionally chrome-free (data-mode="live"), so iframing
            it gives the host an honest feel for what the room will see. */}
        <div
          data-testid="leave-your-mark-preview"
          style={{
            marginTop: 28,
            borderRadius: 6,
            overflow: 'hidden',
            border: `1px solid ${MODE_TWO.hair}`,
            background: '#0a0805',
            // 16:9 wall feel — restrained, not cinematic
            aspectRatio: '16 / 9',
            position: 'relative',
          }}
        >
          <iframe
            src={`${window.location.origin}/live/remember-us/${slug}`}
            title="Remember Us live preview"
            data-testid="leave-your-mark-preview-iframe"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              border: 'none',
              pointerEvents: 'none',
            }}
            loading="lazy"
          />
          <div style={{
            position: 'absolute', bottom: 8, right: 10,
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(255,235,210,0.45)',
            pointerEvents: 'none',
          }}>
            Live preview ✦
          </div>
        </div>
      </GraphitePanel>
    </section>
  );
};


// ─────────────────────────────────────────────────────────────────
//  SETUP TAB
// ─────────────────────────────────────────────────────────────────
const SetupTab = ({
  event, slug, eventUrl, copied, copyLink, shareEvent,
  qrCardRef, savedQrDesign, setShowDesignEditor, downloadQR,
  fetchEvent, onUpdate, showKioskQr, setShowKioskQr, navigate,
  pairingFocusPulse,
}) => {
  // iter195 — Leavers Night dashboards are radically simpler than
  // wedding stewardship. Branding folio, Photo Booth tablet
  // controls, the End Card door, and the legacy "Feature this
  // event" / "Compose the plate" / "Hold the link" SaaS-style
  // affordances are hidden. The room must feel prepared, not
  // configurable. Schools = collective identity, not operator
  // tooling (Belief 5). `isLeaversEdition` is the single source
  // of truth — shared with EventCompanions.js.
  const isLeavers = isLeaversEdition(event);
  return (
    <>
      {/* SHARE WITH GUESTS — the one action the host reaches for ─── */}
      <Section
        eyebrow={isLeavers ? 'Your QR code' : 'Your QR code'}
        title={isLeavers ? 'One QR for the year group' : 'Share with your guests'}
        sub={isLeavers
          ? 'Hand the QR to the year group. They scan once and step straight into the evening.'
          : 'Show this QR at your event. Guests upload from their phones — nothing to install.'}
        testId="setup-share-section"
      >
        <GraphitePanel padding="clamp(22px, 3vw, 32px)">
          <div style={{
            display: 'grid',
            // Mobile-safe — collapses to a single column when the
            // container is narrower than ~540px so the operational
            // column (link + share/copy/compose/save buttons) never
            // bleeds off-screen on phones. Side-by-side returns on
            // desktop. The `min(260px, 100%)` floor lets the QR
            // plate breathe at desktop widths while permitting full
            // collapse on mobile.
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
            gap: 'clamp(22px, 3vw, 36px)',
            alignItems: 'flex-start',
          }}>
            {/* QR plate */}
            <div ref={qrCardRef} data-testid="qr-code-card" style={{
              background: '#F5F0E4',
              border: `1px solid ${MODE_TWO.goldHair}`,
              padding: 22,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
            }}>
              <div style={{
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 10, letterSpacing: '0.24em',
                textTransform: 'uppercase', color: 'rgba(40,32,18,0.55)',
                textAlign: 'center', marginBottom: 12,
              }}>✦ Scan to join</div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <QRCodeSVG value={eventUrl} size={200} level="H" includeMargin
                  fgColor="#1B140A" bgColor="#F5F0E4" data-testid="qr-code" />
              </div>
              <div style={{
                marginTop: 14, textAlign: 'center',
                fontFamily: MODE_TWO.serif, fontSize: 12.5,
                color: 'rgba(40,32,18,0.65)', letterSpacing: '0.02em',
              }}>{event.event_name}</div>
            </div>

            {/* Operational column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <RestrainedMeta>The link</RestrainedMeta>
                <div style={{
                  marginTop: 8, padding: '12px 14px',
                  background: MODE_TWO.bg, border: `1px solid ${MODE_TWO.hair}`,
                  borderRadius: 4,
                  fontFamily: MODE_TWO.mono, fontSize: 12,
                  color: MODE_TWO.inkMid, letterSpacing: '0.01em',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{eventUrl}</div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <QuietPress onClick={shareEvent} testId="organiser-share-hero">
                  Share QR code ✦
                </QuietPress>
                {!isLeavers && (
                  <QuietPress variant="quiet" onClick={copyLink} testId="copy-link-button">
                    {copied ? 'Copied ✦' : 'Copy link ✦'}
                  </QuietPress>
                )}
                {!isLeavers && (
                  <QuietPress variant="quiet" onClick={() => setShowDesignEditor(true)} testId="open-design-editor">
                    Edit the QR code ✦
                  </QuietPress>
                )}
                <QuietPress variant="quiet" onClick={downloadQR} testId="download-qr-button">
                  Download QR ✦
                </QuietPress>
                <QuietPress
                  variant="quiet"
                  onClick={() => window.open(`/operator/smoke/${slug}`, '_blank', 'noopener')}
                  testId="preflight-check-btn"
                >
                  Preview as a guest ✦
                </QuietPress>
              </div>

              {!isLeavers && (
                <div style={{ marginTop: 4 }}>
                  <FeatureEventToggle event={event} onUpdate={fetchEvent} />
                </div>
              )}
            </div>
          </div>
        </GraphitePanel>
      </Section>

      {/* COMPANIONS — the full edition surface, rendered as visual
          cards rather than a list. Each card distinguishes
          included / commissioned / latent without using the
          banned "Pro/Upgrade/Premium" badges. */}
      <Section
        eyebrow="What's included ✦"
        title="Your event package"
        sub="Every product the night ships with, named clearly. Tap any card to open the experience."
        testId="setup-companions-section"
      >
        <EventCompanionsModeTwo event={event} testId="setup-companions-grid" />
      </Section>

      {/* iter195 — LEAVE YOUR MARK ✦
          Remember Us as emotional centrepiece. Only on Leavers Night.
          The shared wall is the participation substrate for schools
          (Belief 2/5) — collective identity, written by the year
          group together. Visually outranks Drop One Word. */}
      {isLeavers && (
        <LeaveYourMarkSection event={event} slug={slug} />
      )}

      {/* TONIGHT'S ATMOSPHERE ✦ — Drop One Word host widget */}
      {((event?.features || []).includes('drop_one_word') || event?.drop_one_word_purchased) && (
        <Section
          eyebrow="Live atmosphere"
          title="The room, in its own words"
          sub="Live feelings from the night — every word a guest has dropped, the most repeated one, and the latest five in."
          testId="setup-drop-one-word-section"
        >
          <DropOneWordHostWidget event={event} />
        </Section>
      )}

      {/* LIVE MIRROR ✦ — operational visibility for the host */}
      {((event?.features || []).includes('live_screen') || event?.live_screen_purchased) && (
        <Section
          eyebrow="What's on screen at the venue"
          title="Live screen preview"
          sub="A silent, faithful view of the projector — so you can see the room's atmosphere as it unfolds, without leaving Setup."
          testId="setup-live-mirror-section"
        >
          <LiveMirrorPreview event={event} />
        </Section>
      )}

      {/* INVITES + PREVIEW + GUESTBOOK — quiet stewardship affordances */}
      <Section
        eyebrow={isLeavers ? 'Doors into the evening' : 'Tools'}
        title={isLeavers ? 'What the year group steps into' : 'Manage your event'}
        sub={isLeavers
          ? 'Quiet entrances to the rooms the evening already holds open.'
          : 'Open the rooms the night needs you to keep open.'}
        testId="setup-doors-section"
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}>
          <DoorTile
            title="Invites"
            subtitle={isLeavers ? 'Invitations & RSVPs for the year group' : 'Compose & send invitations'}
            onClick={() => navigate(`/event/${slug}/invites`)}
            testId="invites-link-btn"
          />
          <DoorTile
            title="Guestbook"
            subtitle="Voice messages from the room"
            onClick={() => window.open(`${window.location.origin}/event/${slug}/guestbook`, '_blank')}
            testId="guestbook-link-btn"
          />
          <DoorTile
            title="Preview the guest view"
            subtitle={isLeavers ? 'See what the year group will see' : 'See what guests will see'}
            onClick={() => navigate(`/event/${slug}/preview`)}
            testId="preview-link-btn"
          />
          {!isLeavers && (
            <DoorTile
              title="End card"
              subtitle="The closing frame"
              onClick={() => navigate(`/events/${event.event_id}/endcard`)}
              testId="endcard-settings-btn"
            />
          )}
        </div>
      </Section>

      {/* BRANDING FOLIO — quiet editorial customisation.
          Hidden on Leavers Night per iter195: the room is
          pre-composed, not a configurator. */}
      {!isLeavers && (
        <Section
          eyebrow="Your branding"
          title="Branding"
          sub="A restrained accent and a line of authored tone. Guests notice the calmness, not the colour."
          testId="setup-branding-section"
        >
          <BrandingFolio event={event} onUpdate={onUpdate} />
        </Section>
      )}

      {/* CAPTURE TOOLS — Booth & Pocket Booth as quiet folios.
          On Leavers Night the Photo Booth folio is hidden (tablet
          booth is not part of the schools package) — only Pocket
          Booth surfaces, since it is included in the £99 edition. */}
      <Section
        eyebrow={isLeavers ? 'Companions on the night' : 'Capture tools'}
        title={isLeavers ? 'What guests carry in their pockets' : 'Devices at the venue'}
        sub={isLeavers
          ? 'A multi-device booth the year group opens from their own phones — no tablet, no setup.'
          : 'Optional companions for the night — a tablet booth or a multi-device pocket booth.'}
        testId="setup-booth-section"
      >
        {!isLeavers && <BoothPairingPanel event={event} focusPulse={pairingFocusPulse} />}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 12,
        }}>
          {!isLeavers && (
            <BoothFolio
              title="Photo Booth"
              sub="Turn any tablet into a booth — feeds the event film."
              actions={[
                { label: 'Open the booth ✦', onClick: () => window.open(`${window.location.origin}/event/${slug}/booth`, '_blank'), testId: 'booth-launch-btn' },
                { label: 'Open the big screen', onClick: () => window.open(`${window.location.origin}/event/${slug}/live`, '_blank'), variant: 'quiet', testId: 'live-launch-btn' },
              ]}
            />
          )}
          <BoothFolio
            title="Pocket Booth"
            sub="Multi-device sync capture · live gallery · kiosk QR."
            footer={<KioskStatusPill eventId={event.event_id} />}
            actions={
              event.pocket_booth_purchased ? [
                { label: 'Open the pocket booth ✦', onClick: () => navigate(`/event/${slug}/pocket-booth`), testId: 'pocket-booth-launch-btn' },
                { label: 'Live gallery', onClick: () => navigate(`/event/${slug}/pocket-gallery`), variant: 'quiet', testId: 'pocket-gallery-btn' },
                { label: 'Kiosk plate', onClick: () => setShowKioskQr(true), variant: 'quiet', testId: 'kiosk-qr-button' },
              ] : [
                { label: 'Commission the Pocket Booth ✦', onClick: () => navigate(`/checkout?products=pocket_booth&event_id=${event.event_id}`), testId: 'pocket-booth-buy-btn' },
              ]
            }
          />
        </div>
      </Section>

      {/* GUEST WELCOME ✦ — the single doorway into the evening */}
      <Section
        eyebrow="Guest Welcome ✦"
        title="One QR for the whole evening"
        sub="Seating, menu, uploads, booth and memories — guests scan once and step straight into tonight ✦"
        testId="setup-guest-access-section"
      >
        <GuestAccessSetup event={event} onUpdate={onUpdate} eventUrl={eventUrl} />
      </Section>
    </>
  );
};

const DoorTile = ({ title, subtitle, onClick, testId }) => {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      style={{
        textAlign: 'left',
        padding: '16px 18px',
        background: MODE_TWO.bgPanel,
        border: `1px solid ${pressed ? MODE_TWO.goldPressed : MODE_TWO.hair}`,
        borderRadius: 4,
        cursor: 'pointer',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease',
        fontFamily: MODE_TWO.serif,
        color: MODE_TWO.ink,
      }}
    >
      <div style={{
        fontSize: 14.5, letterSpacing: '0.01em',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
      }}>
        <span>{title}</span>
        <span style={{ color: MODE_TWO.goldSoft, fontSize: 12 }}>✦</span>
      </div>
      <div style={{
        marginTop: 6, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
        fontSize: 12.5, color: MODE_TWO.inkMuted,
      }}>{subtitle}</div>
    </button>
  );
};

const BoothFolio = ({ title, sub, actions = [], footer }) => (
  <GraphitePanel padding="20px 22px">
    <div style={{
      fontFamily: MODE_TWO.serif, fontSize: 16, color: MODE_TWO.ink,
      letterSpacing: '0.005em',
    }}>{title}</div>
    <div style={{
      marginTop: 6, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
      fontSize: 13, color: MODE_TWO.inkMuted, lineHeight: 1.5,
    }}>{sub}</div>
    {footer && <div style={{ marginTop: 10 }}>{footer}</div>}
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
      {actions.map((a) => (
        <QuietPress key={a.label} variant={a.variant || 'primary'} onClick={a.onClick} testId={a.testId}>
          {a.label}
        </QuietPress>
      ))}
    </div>
  </GraphitePanel>
);


// ─────────────────────────────────────────────────────────────────
//  BOOTH PAIRING PANEL — Spotify-Connect-style pairing.
//
//  Sits inside the host's "Capture tools" section. Shows the current
//  6-digit code (or a quiet "Mint a code ✦" CTA if none yet), with a
//  one-press copy and a discreet rotate option. The booth attendant
//  opens eventdrop.co/booth on the kiosk, types the code, and the
//  tablet locks itself into the night — the host never has to read
//  out an event ID or paste a URL.
// ─────────────────────────────────────────────────────────────────
const BoothPairingPanel = ({ event, focusPulse }) => {
  const [code, setCode] = useState(null);
  const [lastUsedAt, setLastUsedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Re-evaluated on a 30s tick so the "kiosk is awake" line fades
  // out 30 minutes after the booth's last interaction without the
  // host needing to refresh. Observational, not real-time.
  const [, setNowTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/booth-pairing`);
        if (!cancelled) {
          setCode(r.data?.code || null);
          setLastUsedAt(r.data?.last_used_at || null);
        }
      } catch (_) {
        if (!cancelled) { setCode(null); setLastUsedAt(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOnce();
    // Soft poll — every 45s — so a kiosk pairing that happens after
    // the host opens this tab shows up without a manual refresh. No
    // websockets, no heartbeat language; just a quiet re-fetch.
    const pollInterval = setInterval(fetchOnce, 45_000);
    // Independent 30s tick so the line can fade purely on time
    // elapsed even if the server hasn't been re-polled yet.
    const tickInterval = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      clearInterval(tickInterval);
    };
  }, [event.event_id]);

  const mint = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${event.event_id}/booth-pairing/generate`);
      setCode(r.data.code);
      // A freshly minted code has never been redeemed — wipe any
      // stale "kiosk is awake" line from the previous code.
      setLastUsedAt(null);
    } catch (e) {
      brandToast.slipped('The code didn\u2019t mint · please try again');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {}
  };

  // Format `482193` → `482 193` for a calmer read on the night.
  const display = code ? `${code.slice(0, 3)} ${code.slice(3)}` : '— — — — — —';

  // ── "A kiosk is awake" reassurance ─────────────────────────────
  // True iff the *current* active code has been redeemed within the
  // last 30 minutes. Drives a single italic line on the Setup card —
  // no green dot, no pulse, no timestamp, no count. The 30s render
  // tick (above) re-evaluates this so the line fades out by itself
  // once the booth has been quiet for half an hour.
  const AWAKE_WINDOW_MS = 30 * 60 * 1000;
  const boothAwake = (() => {
    if (!code || !lastUsedAt) return false;
    const used = Date.parse(lastUsedAt);
    if (Number.isNaN(used)) return false;
    return (Date.now() - used) < AWAKE_WINDOW_MS;
  })();

  // The same code, encoded for camera-scan entry. Hosted on the public
  // `/booth?code=...` route so the kiosk shell auto-redeems on arrival.
  const qrOrigin = (typeof window !== 'undefined' ? window.location.origin : 'https://eventdrop.co');
  const qrUrl = code ? `${qrOrigin}/booth?code=${code}` : null;

  return (
    <GraphitePanel testId="booth-pairing-panel" padding="22px 24px" style={{ marginBottom: 12 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 22, alignItems: 'center',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: MODE_TWO.goldSoft, marginBottom: 8,
          }}>✦ BOOTH OS — VENUE KIOSK PAIRING</div>
          <div
            data-testid="booth-pairing-code"
            className={focusPulse ? 'booth-pairing-code--pulse' : undefined}
            style={{
              fontFamily: MODE_TWO.serif, fontWeight: 500,
              fontSize: 'clamp(28px, 4vw, 38px)',
              color: code ? MODE_TWO.ink : MODE_TWO.inkFaint,
              letterSpacing: '0.08em',
              lineHeight: 1.05,
              // Soft inline base style — the keyframe animation is
              // class-bound and only fires when focusPulse is on.
              transition: 'text-shadow 600ms ease',
              borderRadius: 4,
              padding: '4px 6px',
              margin: '-4px -6px',
            }}
          >
            {loading ? '— — — — — —' : display}
          </div>
          {/* Pulse keyframes — scoped here so the BoothPairingPanel
              owns its own visual behaviour. The animation runs twice
              (~2.4s total) then resets cleanly. The class is removed
              by EventPage after the timer so no orphan state. */}
          {focusPulse && (
            <style>{`
              @keyframes boothPairingCodePulse {
                0%   { text-shadow: 0 0 0 rgba(212,170,82,0);   box-shadow: 0 0 0 0 rgba(212,170,82,0); }
                40%  { text-shadow: 0 0 16px rgba(212,170,82,0.55); box-shadow: 0 0 0 1px rgba(212,170,82,0.35), 0 0 32px 4px rgba(212,170,82,0.18); }
                100% { text-shadow: 0 0 0 rgba(212,170,82,0);   box-shadow: 0 0 0 0 rgba(212,170,82,0); }
              }
              .booth-pairing-code--pulse {
                animation: boothPairingCodePulse 1.2s ease-in-out 2;
              }
            `}</style>
          )}
          <div style={{
            marginTop: 10, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 13, color: MODE_TWO.inkMuted, lineHeight: 1.5,
          }}>
            {code
              ? <>Open <strong style={{ color: MODE_TWO.ink, fontStyle: 'normal' }}>eventdrop.co/booth</strong> on the kiosk and enter this code ✦</>
              : <>Mint a code, then open <strong style={{ color: MODE_TWO.ink, fontStyle: 'normal' }}>eventdrop.co/booth</strong> on the kiosk to pair this night ✦</>}
          </div>

          {/* Quiet preview affordance — opens the kiosk surface in
              a new tab so the host can see exactly what guests
              will experience at the venue, without disturbing a
              live pairing. Restrained: italic gold link, no button
              chrome. Always visible (the kiosk renders even before
              a pairing code is minted). */}
          <a
            href={`/booth-os/${event.event_id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booth-pairing-preview-booth-os"
            style={{
              display: 'inline-block',
              marginTop: 10,
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 12.5, letterSpacing: '0.01em',
              color: MODE_TWO.goldSoft,
              textDecoration: 'underline',
              textDecorationColor: 'rgba(212,170,82,0.32)',
              textUnderlineOffset: 4,
            }}
          >
            Preview Booth OS ✦
          </a>

          {/* "A kiosk is awake" — passive operational reassurance.
              Only renders when the current code has been redeemed
              within the last 30 minutes. Observational, not technical:
              no green dot, no pulse, no timestamp, no count. Fades on
              its own as the 30s tick re-evaluates `boothAwake`. */}
          {boothAwake && (
            <div
              data-testid="booth-pairing-awake"
              style={{
                marginTop: 12,
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 12.5, color: MODE_TWO.goldSoft,
                letterSpacing: '0.04em',
                opacity: 0.85,
              }}
            >
              ✦ A kiosk is awake
            </div>
          )}

          {/* Quiet camera-scan affordance. Kept deliberately small and
              gold-tinted so it whispers "there's an even easier way"
              without feeling like a giant tech feature. Operators who
              don't notice it lose nothing — the keypad remains primary. */}
          {qrUrl && (
            <div
              data-testid="booth-pairing-qr"
              style={{
                marginTop: 18,
                display: 'inline-flex', alignItems: 'center', gap: 14,
                padding: '10px 14px 10px 10px',
                background: '#F5F0E4',
                border: `1px solid ${MODE_TWO.goldHair}`,
                borderRadius: 4,
              }}
            >
              <QRCodeSVG
                value={qrUrl}
                size={68}
                level="M"
                fgColor="#3F2A14"
                bgColor="#F5F0E4"
                data-testid="booth-pairing-qr-svg"
              />
              <span style={{
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 12.5, color: '#5A3F1F', letterSpacing: '0.02em',
                lineHeight: 1.4, maxWidth: 220,
              }}>
                Scan to bring this booth into the night ✦
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          {code ? (
            <>
              <QuietPress onClick={copy} testId="booth-pairing-copy">
                {copied ? 'Held ✦' : 'Hold the code'}
              </QuietPress>
              <QuietPress variant="quiet" onClick={mint} testId="booth-pairing-rotate">
                {busy ? 'Minting\u2026' : 'Mint a new one'}
              </QuietPress>
            </>
          ) : (
            <QuietPress onClick={mint} testId="booth-pairing-mint">
              {busy ? 'Minting\u2026' : 'Mint a pairing code ✦'}
            </QuietPress>
          )}
        </div>
      </div>
    </GraphitePanel>
  );
};




// ─────────────────────────────────────────────────────────────────
//  BRANDING FOLIO — quiet editorial customisation (no SaaS form)
// ─────────────────────────────────────────────────────────────────
const BrandingFolio = ({ event, onUpdate }) => {
  const [branding, setBranding] = useState(event?.branding || {});
  const [saving, setSaving] = useState(false);

  const save = async (field, value) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/events/${event.event_id}/branding`, { [field]: value });
      setBranding(res.data.branding);
      onUpdate?.();
      brandToast.gentle('Branding settled');
    } catch (e) {
      brandToast.slipped("That couldn't be updated · please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <GraphitePanel testId="branding-panel" padding="22px 24px">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 22,
      }}>
        {/* Accent */}
        <div>
          <RestrainedMeta>Accent</RestrainedMeta>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="color"
              value={branding.accent_color || MODE_TWO.gold}
              onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
              data-testid="branding-color"
              style={{
                width: 38, height: 38, border: `1px solid ${MODE_TWO.hair}`,
                borderRadius: 4, background: 'transparent', cursor: 'pointer',
              }}
            />
            <QuietPress variant="quiet"
              onClick={() => save('accent_color', branding.accent_color)}
              testId="save-color">
              {saving ? 'Settling…' : 'Settle ✦'}
            </QuietPress>
          </div>
        </div>

        {/* Tagline */}
        <div>
          <RestrainedMeta>Tagline</RestrainedMeta>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={branding.tagline || ''}
              placeholder="A quiet line for the night"
              onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
              data-testid="branding-tagline"
              style={{
                flex: 1, padding: '10px 12px',
                background: MODE_TWO.bg, border: `1px solid ${MODE_TWO.hair}`,
                borderRadius: 4, color: MODE_TWO.ink,
                fontFamily: MODE_TWO.serif, fontStyle: 'italic', fontSize: 13.5,
                outline: 'none',
              }}
            />
            <QuietPress variant="quiet"
              onClick={() => save('tagline', branding.tagline || '')}
              testId="save-tagline">Save ✦</QuietPress>
          </div>
        </div>

        {/* Font */}
        <div>
          <RestrainedMeta>Type</RestrainedMeta>
          <select
            value={branding.font || 'Playfair Display'}
            onChange={(e) => { setBranding({ ...branding, font: e.target.value }); save('font', e.target.value); }}
            data-testid="branding-font"
            style={{
              marginTop: 10, width: '100%', padding: '10px 12px',
              background: MODE_TWO.bg, border: `1px solid ${MODE_TWO.hair}`,
              borderRadius: 4, color: MODE_TWO.ink,
              fontFamily: MODE_TWO.serif, fontSize: 13.5,
              outline: 'none',
            }}
          >
            <option value="Playfair Display">Playfair Display</option>
            <option value="Work Sans">Work Sans</option>
            <option value="Inter">Inter</option>
          </select>
        </div>
      </div>
    </GraphitePanel>
  );
};


// ─────────────────────────────────────────────────────────────────
//  WALL TAB
// ─────────────────────────────────────────────────────────────────
const WallTab = ({
  event, photos, filteredPhotos, galleryFilter, setGalleryFilter,
  uploading, dragActive, handleUpload, handleDrag, handleDrop,
  fileInputRef, setLightboxPhoto, downloadPhoto, downloadZip,
  downloadingZip, newPhotoCount, navigate, slug,
}) => {
  const accentColor = MODE_TWO.gold;
  return (
    <>
      {/* UPLOAD DROPZONE — Mode 2 graphite, no purple gradient ──── */}
      <Section
        eyebrow="Memory Wall"
        title="Add to the Memory Wall"
        sub="Photos, videos and messages land here as the night unfolds. Drag in, or browse to choose."
        testId="wall-upload-section"
      >
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
          style={{
            padding: 'clamp(40px, 6vw, 64px) 24px',
            background: MODE_TWO.bgPanel,
            border: `1px dashed ${dragActive ? MODE_TWO.goldMute : MODE_TWO.hair}`,
            borderRadius: 6,
            textAlign: 'center', cursor: 'pointer',
            transition: 'border-color 220ms ease, background 220ms ease',
          }}
        >
          <div style={{
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 10.5, letterSpacing: '0.26em', textTransform: 'uppercase',
            color: MODE_TWO.goldSoft,
          }}>✦ Drop here</div>
          <div style={{
            marginTop: 14, fontFamily: MODE_TWO.serif,
            fontSize: 'clamp(18px, 2.2vw, 22px)', color: MODE_TWO.ink,
            letterSpacing: '-0.005em',
          }}>{uploading ? 'Pinning to the wall…' : 'Photos & videos to the wall'}</div>
          <div style={{
            marginTop: 8, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 13, color: MODE_TWO.inkMuted,
          }}>or click to browse · max 50MB per moment</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            style={{ display: 'none' }}
            onChange={(e) => handleUpload(e.target.files)}
            data-testid="file-input"
          />
        </div>
      </Section>

      {/* GALLERY ───────────────────────────────────────────────── */}
      <Section
        eyebrow="Uploaded so far"
        title="Gallery"
        sub={photos.length === 0 ? 'Nothing has landed yet — the wall is still settling.' : undefined}
        testId="wall-gallery-section"
        action={photos.length > 0 ? (
          <QuietPress
            variant="quiet"
            onClick={downloadZip}
            testId="download-zip-button"
            style={downloadingZip ? { opacity: 0.6 } : undefined}
          >
            {downloadingZip ? 'Preparing…' : 'Save the collection ✦'}
          </QuietPress>
        ) : null}
      >
        {/* Filter row */}
        {photos.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }} data-testid="gallery-filters">
            {[
              { id: 'all',        label: 'All',        count: photos.length },
              { id: 'photos',     label: 'Photos',     count: photos.filter(p => p.content_type?.startsWith('image/') && p.source !== 'guestbook' && p.source !== 'challenge' && p.source !== 'lens_note' && p.source !== 'one_word').length },
              { id: 'videos',     label: 'Videos',     count: photos.filter(p => (p.content_type?.startsWith('video/') || p.source === 'lens_note') && p.source !== 'guestbook' && p.source !== 'challenge').length },
              { id: 'messages',   label: 'Messages',   count: photos.filter(p => p.source === 'guestbook' || p.source === 'one_word').length },
              { id: 'challenges', label: 'Challenges', count: photos.filter(p => p.source === 'challenge').length },
            ].filter(f => f.count > 0 || f.id === 'all').map((f) => {
              const active = galleryFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setGalleryFilter(f.id)}
                  data-testid={`filter-${f.id}`}
                  style={{
                    padding: '8px 14px',
                    background: active ? MODE_TWO.bgPanelHi : 'transparent',
                    border: `1px solid ${active ? MODE_TWO.goldMute : MODE_TWO.hair}`,
                    borderRadius: 4,
                    fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                    fontSize: 12.5,
                    color: active ? MODE_TWO.ink : MODE_TWO.inkMuted,
                    cursor: 'pointer',
                    transition: 'background 220ms ease, border-color 220ms ease',
                    letterSpacing: '0.01em',
                  }}
                >
                  {f.label} <span style={{ color: MODE_TWO.inkFaint, fontSize: 11 }}>· {f.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {newPhotoCount > 0 && (
          <div data-testid="new-photo-indicator" style={{
            marginBottom: 14,
            padding: '10px 14px',
            background: MODE_TWO.bgRaised,
            border: `1px solid ${MODE_TWO.goldHair}`,
            borderRadius: 4,
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 13, color: MODE_TWO.inkMid,
          }}>
            <span style={{ color: MODE_TWO.gold }}>✦</span>{' '}
            {newPhotoCount === 1 ? 'A new moment has landed' : `${newPhotoCount} new moments have landed`}
          </div>
        )}

        {filteredPhotos.length === 0 && photos.length === 0 ? (
          <GraphitePanel testId="no-photos-message" padding="40px 24px">
            <div style={{
              textAlign: 'center',
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 14.5, color: MODE_TWO.inkMuted,
            }}>The wall is still settling. The first moment will land soon ✦</div>
          </GraphitePanel>
        ) : filteredPhotos.length === 0 ? (
          <GraphitePanel testId="no-filtered-photos" padding="32px 24px">
            <div style={{
              textAlign: 'center',
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 14, color: MODE_TWO.inkMuted,
            }}>Nothing here yet within {galleryFilter} ✦</div>
          </GraphitePanel>
        ) : (
          <div
            data-testid="photo-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 8,
            }}
          >
            {filteredPhotos.map((photo) => {
              const isVideo = photo.content_type?.startsWith('video/') || photo.source === 'lens_note';
              const isAudio = photo.content_type?.startsWith('audio/');
              // iter183 — text-only contributions (Drop One Word ✦,
              // and any other source emitting `text/plain` without a
              // storage_path) get a card-shaped tile that displays
              // the word/message in editorial type — no thumbnail,
              // no broken-image icon.
              const isText = photo.content_type === 'text/plain' || !photo.storage_path;
              const isLensNote = photo.source === 'lens_note';
              const isOneWord = photo.source === 'one_word';
              return (
                <button
                  key={photo.photo_id}
                  type="button"
                  onClick={() => setLightboxPhoto(photo)}
                  data-testid={`photo-${photo.photo_id}`}
                  style={{
                    position: 'relative', padding: 0,
                    background: MODE_TWO.bgPanel,
                    border: `1px solid ${MODE_TWO.hair}`,
                    borderRadius: 3, overflow: 'hidden',
                    cursor: 'pointer',
                    aspectRatio: isVideo ? '16 / 10' : '1 / 1',
                    gridColumn: isVideo ? 'span 2' : 'auto',
                  }}
                >
                  {isText ? (
                    <div style={{
                      width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: MODE_TWO.bgRaised, color: MODE_TWO.ink,
                      padding: '12px 14px', gap: 8,
                    }}>
                      <span style={{ color: MODE_TWO.gold, fontSize: 14 }}>✦</span>
                      <div style={{
                        fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                        fontSize: 17, textAlign: 'center',
                        lineHeight: 1.25, color: MODE_TWO.ink,
                      }}>
                        {photo.word || photo.message || ''}
                      </div>
                      <div style={{
                        fontFamily: MODE_TWO.serif, fontSize: 9.5,
                        letterSpacing: '0.18em', textTransform: 'uppercase',
                        color: MODE_TWO.inkFaint,
                      }}>{photo.guest_name || (isOneWord ? 'a guest' : '')}</div>
                    </div>
                  ) : isAudio ? (
                    <div style={{
                      width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: MODE_TWO.bgRaised, color: MODE_TWO.inkMid,
                      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                      fontSize: 12, gap: 6,
                    }}>
                      <span style={{ color: MODE_TWO.gold, fontSize: 18 }}>✦</span>
                      <span>A voice message</span>
                      <span style={{ fontStyle: 'normal', fontSize: 10, color: MODE_TWO.inkFaint }}>
                        {photo.guest_name || 'a guest'}
                      </span>
                    </div>
                  ) : isVideo ? (
                    <video src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)}
                      poster={photo.poster_path ? getMediaUrl(photo.poster_path, `${API}/photos/${photo.poster_path}`) : undefined}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      muted loop playsInline preload="metadata" />
                  ) : (
                    <img src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)}
                      alt={photo.original_filename}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  {/* Tiny corner labels — restrained */}
                  {(isVideo || isAudio || photo.source === 'guestbook' || isOneWord) && (
                    <div style={{
                      position: 'absolute', top: 6, left: 6,
                      padding: '2px 6px',
                      background: 'rgba(15,16,20,0.78)',
                      border: `1px solid ${MODE_TWO.goldHair}`,
                      color: MODE_TWO.inkMid,
                      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                      fontSize: 9.5, letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}>{isLensNote ? 'lens note' : isOneWord ? 'word' : isVideo ? 'reel' : isAudio ? 'voice' : 'message'}</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
};


// ─────────────────────────────────────────────────────────────────
//  ARCHIVE TAB — bookshelf, chapters, memory book, ZIP
// ─────────────────────────────────────────────────────────────────
const ArchiveTab = ({ event, slug, navigate, setShowChaptersModal }) => {
  const hasMemorySnap = Array.isArray(event?.features) && event.features.includes('memory_snap');
  return (
    <>
      <Section
        eyebrow="Archive"
        title="Your archived events"
        sub="The bookshelf the host returns to. Chapters and letters settle here as the night gathers."
        testId="archive-bookshelf-section"
        action={hasMemorySnap ? (
          <QuietPress variant="quiet" onClick={() => navigate(`/event/${slug}/archive`)} testId="visit-archive-btn">
            Visit the archive ✦
          </QuietPress>
        ) : null}
      >
        {hasMemorySnap ? (
          <GraphitePanel padding="22px 24px" testId="organiser-bookshelf-widget">
            <Bookshelf
              eventId={event.event_id}
              slug={slug}
              variant="compact"
              shelfHeight={180}
              showDust={false}
              onPullChapter={() => navigate(`/event/${slug}/archive`)}
              onPullLetter={() => navigate(`/event/${slug}/archive`)}
            />
            <div style={{
              marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}>
              <MemoryRecapPill event={event} />
              <QuietPress variant="quiet"
                onClick={() => setShowChaptersModal(true)}
                testId="open-chapters-modal-btn">
                Add a chapter ✦
              </QuietPress>
              <QuietPress variant="quiet"
                onClick={() => navigate(`/event/${encodeURIComponent(slug)}/artifacts/polaroid-strip`)}
                testId="open-polaroid-studio-btn">
                Create a polaroid strip ✦
              </QuietPress>
              <EventPosterPill event={event} />
            </div>
          </GraphitePanel>
        ) : (
          <GraphitePanel padding="32px 24px">
            <div style={{
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 14, color: MODE_TWO.inkMuted, lineHeight: 1.6,
            }}>The archive lives with the Memory Snap edition. Once enabled, chapters and letters settle here.</div>
          </GraphitePanel>
        )}
      </Section>

      <Section
        eyebrow="Live preview"
        title="Memory Wall preview"
        sub="A quiet ambient preview of what is taking shape on the night."
        testId="archive-wall-preview-section"
      >
        <MemoryWallPreviewTile event={event} />
      </Section>

      <Section
        eyebrow="Keepsake"
        title="Memory Book"
        sub="An A5 PDF keepsake composed from the night's photos and messages."
        testId="archive-memorybook-section"
      >
        <MemoryBookModule event={event} />
      </Section>
    </>
  );
};


// ─────────────────────────────────────────────────────────────────
//  RECAP TAB — film, highlight, slideshow, share kit
// ─────────────────────────────────────────────────────────────────
const RecapTab = ({ event, photos, slug, navigate }) => {
  const isProm = event.type === 'prom';
  const mediaCount = useMemo(() => ({
    photos: photos.filter(p => p.content_type?.startsWith('image/')).length,
    videos: photos.filter(p => p.content_type?.startsWith('video/')).length,
  }), [photos]);

  return (
    <>
      {!isProm && (
        <Section
          eyebrow="Your event film"
          title="Your event film"
          sub="A composed cinematic record of the night, drawn from every moment on the wall."
          testId="recap-film-section"
        >
          <FilmModule event={event} mediaCount={mediaCount} />
        </Section>
      )}

      <Section
        eyebrow={isProm ? 'Highlight reel' : 'Highlight reel'}
        title={isProm ? 'Prom highlight reel' : 'Highlight reel'}
        sub="A shorter cut — the gathering's pulse in under a minute."
        testId="recap-highlight-section"
      >
        {isProm ? (
          <PromHighlightBanner event={event} />
        ) : (event.film?.status === 'ready' || event.film_purchased || event.package_type === 'premium') ? (
          <HighlightModule event={event} />
        ) : (
          <GraphitePanel padding="28px 24px">
            <div style={{
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 14, color: MODE_TWO.inkMuted, lineHeight: 1.6,
            }}>The highlight will be composed once the film is ready ✦</div>
          </GraphitePanel>
        )}
      </Section>

      {!isProm && (
        <Section
          eyebrow="Slideshow"
          title="Venue slideshow"
          sub="A long-playing slideshow that can be projected during the night itself."
          testId="recap-slideshow-section"
        >
          <SlideshowModule event={event} />
        </Section>
      )}

      {/* Pro refinement — reframed as a quiet editorial offer */}
      {!event.has_pro_upgrade && (
        <Section
          eyebrow="Upgrade"
          title="The Pro mark"
          sub="A quieter signature on every frame — the EventDrop watermark removed, the music library widened."
          testId="recap-pro-section"
        >
          <GraphitePanel padding="22px 24px" testId="pro-upgrade-card" data-focus="pro">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 13.5, color: MODE_TWO.inkMid, lineHeight: 1.6,
                maxWidth: 520,
              }}>
                One-time refinement · £9.99 · removes the platform mark from every photo and opens the curated music library.
              </div>
              <QuietPress
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('session_token');
                    const r = await axios.post(`${API}/events/${event.event_id}/purchase-pro-upgrade`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (r.data?.url) window.location.href = r.data.url;
                  } catch (err) {
                    brandToast.slipped(err.response?.data?.detail || "That couldn't be commissioned · please try again");
                  }
                }}
                testId="pro-upgrade-btn"
              >Upgrade to Pro ✦</QuietPress>
            </div>
          </GraphitePanel>
        </Section>
      )}

      {event.has_pro_upgrade && (
        <Section
          eyebrow="Pro mark"
          title="Active"
          sub="The platform mark is removed · the curated music library is open."
          testId="recap-pro-active-section"
        >
          <GraphitePanel padding="18px 22px" testId="pro-active-card" data-focus="pro">
            <div style={{
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13.5, color: MODE_TWO.inkMid,
            }}><span style={{ color: MODE_TWO.gold }}>✦</span> The Pro mark is settled in.</div>
          </GraphitePanel>
        </Section>
      )}

      {/* Add-on folio — quiet editorial offer, no upsell language */}
      <Section
        eyebrow="Optional add-ons"
        title="Optional extras"
        sub="A handful of refinements that may serve a particular evening — never required."
        testId="recap-addons-section"
      >
        <AddonsFolio event={event} />
      </Section>

      {!isProm && (
        <Section
          eyebrow="Share onward"
          title="Share with others"
          sub="A small kit for sharing the night onward — for the guests who weren't there too."
          testId="recap-share-kit-section"
        >
          <ShareKit event={event} />
        </Section>
      )}
    </>
  );
};


// ─────────────────────────────────────────────────────────────────
//  ADDONS FOLIO — Mode 2 quiet editorial offer
// ─────────────────────────────────────────────────────────────────
const ADDONS = [
  { slug: 'live_screen',        name: 'Live screen',         price: 19,    desc: 'A full-screen mode for the venue · guest photos cycle in turn.', flag: 'live_screen_purchased' },
  { slug: 'instant_film',       name: 'Instant film',        price: 4.99,  desc: 'The film is composed at once · no queue.',                       flag: 'priority_render' },
  { slug: 'designer_templates', name: 'Designer templates',  price: 14.99, desc: 'Hand-composed booth backgrounds for weddings & corporate.',     flag: 'designer_templates_purchased' },
  { slug: 'wedding_kit',        name: 'Wedding kit',         price: 39,    desc: 'Live screen · instant film · designer templates · the Pro mark.', flag: null, bundle: true },
];

const AddonsFolio = ({ event }) => {
  const purchase = async (slug) => {
    try {
      const token = localStorage.getItem('session_token');
      const r = await axios.post(`${API}/events/${event.event_id}/purchase-addon/${slug}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      brandToast.slipped(err.response?.data?.detail || "That couldn't be commissioned · please try again");
    }
  };
  const isActive = (a) => {
    if (a.bundle) {
      return event.live_screen_purchased && event.priority_render
        && event.designer_templates_purchased && event.has_pro_upgrade;
    }
    return !!event[a.flag];
  };
  return (
    <GraphitePanel padding="22px 24px" testId="addon-shelf">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {ADDONS.map((a, idx) => {
          const active = isActive(a);
          return (
            <React.Fragment key={a.slug}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 16, alignItems: 'center',
              }} data-testid={`addon-${a.slug}`}>
                <div>
                  <div style={{
                    fontFamily: MODE_TWO.serif, fontSize: 14.5,
                    color: MODE_TWO.ink, letterSpacing: '0.005em',
                  }}>{a.name} <span style={{ color: MODE_TWO.goldSoft, fontSize: 12 }}>· £{a.price}</span></div>
                  <div style={{
                    marginTop: 4, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                    fontSize: 12.5, color: MODE_TWO.inkMuted, lineHeight: 1.5,
                  }}>{a.desc}</div>
                </div>
                <div>
                  {active ? (
                    <span data-testid={`addon-${a.slug}-active`} style={{
                      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                      fontSize: 12.5, color: MODE_TWO.gold, letterSpacing: '0.01em',
                    }}>In place ✦</span>
                  ) : (
                    <QuietPress
                      variant="quiet"
                      onClick={() => purchase(a.slug)}
                      testId={`addon-${a.slug}-buy`}
                    >Commission ✦</QuietPress>
                  )}
                </div>
              </div>
              {idx < ADDONS.length - 1 && <MutedDivider tone={MODE_TWO.hairSoft} />}
            </React.Fragment>
          );
        })}
      </div>
    </GraphitePanel>
  );
};


// ─────────────────────────────────────────────────────────────────
//  LIGHTBOX — Mode 2 dark overlay
// ─────────────────────────────────────────────────────────────────
const Lightbox = ({ photo, onClose, downloadPhoto }) => {
  if (!photo) return null;
  const isAudio = photo.content_type?.startsWith('audio/');
  const isVideo = photo.content_type?.startsWith('video/');
  return (
    <div
      onClick={onClose}
      data-testid="lightbox-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(7,8,11,0.94)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ position: 'relative', maxWidth: 1100, width: '100%' }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: -38, right: 0,
            background: 'transparent', border: `1px solid ${MODE_TWO.hair}`,
            color: MODE_TWO.inkMuted,
            width: 30, height: 30, borderRadius: 4,
            cursor: 'pointer', fontSize: 14,
          }}
        >✕</button>
        {isAudio ? (
          <div onClick={(e) => e.stopPropagation()} style={{
            padding: 'clamp(40px, 6vw, 64px)',
            background: MODE_TWO.bgPanel,
            border: `1px solid ${MODE_TWO.hair}`,
            borderRadius: 4,
            textAlign: 'center',
          }}>
            <div style={{ color: MODE_TWO.gold, fontSize: 32 }}>✦</div>
            <div style={{
              marginTop: 12, fontFamily: MODE_TWO.serif,
              fontSize: 18, color: MODE_TWO.ink,
            }}>A voice message</div>
            <div style={{
              marginTop: 6, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13, color: MODE_TWO.inkMuted,
            }}>{photo.guest_name || 'a guest'}</div>
            <audio
              src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)}
              controls autoPlay
              data-testid="lightbox-audio"
              style={{ marginTop: 24, width: '100%', maxWidth: 440 }}
            />
          </div>
        ) : isVideo ? (
          <video
            src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)}
            controls autoPlay playsInline preload="auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-video"
            style={{
              width: '100%', maxHeight: '82vh', objectFit: 'contain',
              background: MODE_TWO.bg, borderRadius: 4,
            }}
          />
        ) : (
          <img
            src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)}
            alt={photo.original_filename}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxHeight: '82vh', objectFit: 'contain',
              background: MODE_TWO.bg, borderRadius: 4,
            }}
          />
        )}
        <div onClick={(e) => e.stopPropagation()} style={{
          marginTop: 16, display: 'flex', justifyContent: 'center',
        }}>
          <QuietPress
            variant="quiet"
            onClick={() => downloadPhoto(photo)}
            testId="lightbox-download"
          >Save this moment ✦</QuietPress>
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────
//  MAIN
// ─────────────────────────────────────────────────────────────────
const EventPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [celebrationShown, setCelebrationShown] = useState(false);
  const [newPhotoCount, setNewPhotoCount] = useState(0);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [showDesignEditor, setShowDesignEditor] = useState(false);
  const [savedQrDesign, setSavedQrDesign] = useState(null);
  const [showKioskQr, setShowKioskQr] = useState(false);
  const [showChaptersModal, setShowChaptersModal] = useState(false);

  // Lifecycle Observations — fire the host-archive-visit ping the
  // first time the Archive tab is opened in this session. The
  // backend enforces dormancy and idempotency; multiple opens in
  // the same hour produce zero or one reflection regardless.
  const archiveVisitPingedRef = useRef(false);

  // Default tab — honours ?tab=, ?focus= deep-links, else Setup.
  // Tonight ✦ is accepted from the URL only because we want a host
  // to be able to bookmark or be linked into the room; the
  // visibility effect below will reroute if the night isn't live.
  const initialTab = (() => {
    const t = searchParams.get('tab');
    if (t && (TABS.some(x => x.id === t) || t === 'tonight')) return t;
    const f = searchParams.get('focus');
    if (f && FOCUS_TO_TAB[f]) return FOCUS_TO_TAB[f];
    return 'setup';
  })();
  const [tab, setTab] = useState(initialTab);
  // EventDrop Pro ✦ deep-link — when an operator hits the night with
  // ?focus=pairing (Pair Booth ✦ from the studio), we set this flag
  // for ~2.4s so the BoothPairingPanel can flash a faint gold pulse
  // on the 6-digit code. After it fires, the focus useEffect strips
  // the query param so a refresh never replays it.
  const [pairingFocusPulse, setPairingFocusPulse] = useState(false);

  // Tonight ✦ — derived purely from event lifecycle state. The tab
  // surfaces only while the night is *living* (gathering/returning)
  // and quietly retracts before and after. The host doesn't toggle
  // this; the night does.
  const eventState = useMemo(() => deriveEventState(event), [event]);
  const tonightVisible = TONIGHT_VISIBLE_STATES.has(eventState);
  const visibleTabs = useMemo(() => buildTabs(eventState, event), [eventState, event]);

  // If the host was sitting on Tonight ✦ when the night closed
  // (state moved past 'returning' into 'resting'), gently slip them
  // back to Recap — never strand them on a tab that no longer
  // belongs to the room. Guarded on `event` being loaded so a
  // deep-link of `?tab=tonight` isn't clobbered while the event
  // payload is still in-flight (initial render has event=null →
  // state='preparing' → tonightVisible=false, which would otherwise
  // reroute before the real state is known).
  useEffect(() => {
    if (!event) return;
    if (tab === 'tonight' && !tonightVisible) setTab('recap');
  }, [event, tab, tonightVisible]);

  const fileInputRef = useRef(null);
  const qrCardRef = useRef(null);
  const prevPhotoCountRef = useRef(0);

  useEffect(() => { fetchEvent(); }, [slug]);

  // Resolve deep-link focus to a tab once, then strip it from the URL
  // so a refresh doesn't replay.
  useEffect(() => {
    if (!event) return;
    const focus = searchParams.get('focus');
    if (focus && FOCUS_TO_TAB[focus]) {
      setTab(FOCUS_TO_TAB[focus]);
      const FOCUS_TO_FEATURE = {
        film: 'full_event_film', highlight: 'highlight_reel', pro: 'pro',
        guestbook: 'video_guestbook', memory_book: 'memory_guestbook',
        booth: 'photo_booth', pocket_booth: 'pocket_booth',
        challenges: 'challenge_mode', invites: 'invites',
      };
      const featureKey = FOCUS_TO_FEATURE[focus];
      if (featureKey) {
        axios.post(`${API}/events/${event.event_id}/activate-feature`, { feature: featureKey }).catch(() => {});
      }
      // iter185 — after the tab switch, scroll the focus's specific
      // section into view. Without this, deep-linking to e.g.
      // memory_book switched to the Archive tab but landed the user
      // at the TOP of the Archive tab content (Bookshelf section);
      // they had to scroll past the Bookshelf + Wall Preview to find
      // the Memory Book panel — looking like a "page jumps to top"
      // bug from the user's perspective even though the tab change
      // had worked. Adding scroll-to-section closes the UX loop.
      const FOCUS_TO_SCROLL = {
        memory_book:    '[data-testid="archive-memorybook-section"]',
        'memory-book':  '[data-testid="archive-memorybook-section"]',
        film:           '[data-testid="recap-film-section"]',
        highlight:      '[data-testid="recap-highlight-section"]',
        guestbook:      '[data-testid="wall-guestbook-section"]',
        booth:          '[data-testid="wall-booth-section"]',
        pocket_booth:   '[data-testid="wall-pocketbooth-section"]',
        'pocket-booth': '[data-testid="wall-pocketbooth-section"]',
        challenges:     '[data-testid="wall-challenges-section"]',
        invites:        '[data-testid="setup-invites-section"]',
        branding:       '[data-testid="setup-branding-section"]',
        qr:             '[data-testid="setup-qr-section"]',
        share:          '[data-testid="setup-share-section"]',
      };
      const scrollSelector = FOCUS_TO_SCROLL[focus];
      if (scrollSelector) {
        setTimeout(() => {
          const el = document.querySelector(scrollSelector);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 240);
      }
      // EventDrop Pro ✦ — Pair Booth ✦ deep-link. Schedule a scroll
      // to the pairing panel + faint gold pulse on the 6-digit code.
      // The scroll waits a tick for Setup tab content to mount.
      if (focus === 'pairing') {
        setPairingFocusPulse(true);
        setTimeout(() => {
          const el = document.querySelector('[data-testid="booth-pairing-code"]');
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 220);
        // Retract the pulse after the animation has played twice
        // (~2.4s). Matches the soft-glow keyframes in BoothPairingPanel.
        setTimeout(() => setPairingFocusPulse(false), 2600);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('focus'); next.delete('from');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.event_id]);

  // Lifecycle Observations — host-archive-visit ping fires once
  // per session when the Archive tab is opened. Backend enforces
  // auth + dormancy + idempotency; this side only avoids redundant
  // network chatter.
  useEffect(() => {
    if (tab !== 'archive') return;
    if (!event?.event_id) return;
    if (archiveVisitPingedRef.current) return;
    archiveVisitPingedRef.current = true;
    try {
      const tok = localStorage.getItem('session_token');
      if (tok) {
        axios.post(
          `${API}/events/${event.event_id}/archive/host-visit`,
          {},
          { headers: { Authorization: `Bearer ${tok}` } },
        ).catch(() => {});
      }
    } catch { /* noop */ }
  }, [tab, event?.event_id]);

  // Load saved QR design when event loads
  useEffect(() => {
    if (event?.event_id) {
      axios.get(`${API}/events/${event.event_id}/qr-design`)
        .then(res => { if (res.data.elements?.length) setSavedQrDesign(res.data.elements); })
        .catch(() => {});
    }
  }, [event?.event_id]);

  const handleSaveQrDesign = async (designElements) => {
    if (!event?.event_id) return;
    try {
      await axios.put(`${API}/events/${event.event_id}/qr-design`, { elements: designElements });
      setSavedQrDesign(designElements);
      brandToast.gentle('Plate settled');
    } catch { brandToast.slipped("That design couldn't be settled · please try again"); }
  };

  // Quiet polling for new photos — no toast clutter, just a calm
  // "new moments have landed" line surfaced inside the Wall.
  useEffect(() => {
    if (event) {
      fetchPhotos();
      const interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/events/${event.event_id}/photos`);
          const newPhotos = res.data;
          if (newPhotos.length > prevPhotoCountRef.current && prevPhotoCountRef.current > 0) {
            const diff = newPhotos.length - prevPhotoCountRef.current;
            setNewPhotoCount(diff);
            setTimeout(() => setNewPhotoCount(0), 6000);
          }
          prevPhotoCountRef.current = newPhotos.length;
          setPhotos(newPhotos);
        } catch {}
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [event]);

  // Payment polling
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && paymentStatus !== 'paid') {
      setPaymentStatus('polling');
      pollPaymentStatus(sessionId, 0);
    }
    if (searchParams.get('activated') === '1' && !celebrationShown) {
      setPaymentStatus('paid');
      setCelebrationShown(true);
      const next = new URLSearchParams(searchParams);
      next.delete('activated');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 10) {
      setPaymentStatus('timeout');
      brandToast.slipped('The commission is taking a little longer than usual · please refresh');
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        const next = new URLSearchParams(searchParams);
        next.delete('session_id');
        setSearchParams(next, { replace: true });
        fetchEvent();
        if (!celebrationShown) setCelebrationShown(true);
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        brandToast.slipped("That commission has expired · please begin again");
        const next = new URLSearchParams(searchParams);
        next.delete('session_id');
        setSearchParams(next, { replace: true });
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const fetchEvent = async () => {
    try { const res = await axios.get(`${API}/events/slug/${slug}`); setEvent(res.data); }
    catch (e) {
      // Intentionally silent. The not-found render path below already
      // surfaces "That night couldn't be opened ✦" inline as a full
      // panel — firing a toast here doubled the signal and, because
      // this fetch re-fires on every back-navigation / route remount,
      // the toast could stack into a visible queue when guests
      // bounced through history.
    }
    finally { setLoading(false); }
  };

  const fetchPhotos = async () => {
    if (!event) return;
    try {
      const res = await axios.get(`${API}/events/${event.event_id}/photos`);
      const newPhotos = res.data;
      if (prevPhotoCountRef.current > 0 && newPhotos.length > prevPhotoCountRef.current) {
        const diff = newPhotos.length - prevPhotoCountRef.current;
        setNewPhotoCount(diff);
        setTimeout(() => setNewPhotoCount(0), 6000);
      }
      prevPhotoCountRef.current = newPhotos.length;
      setPhotos(newPhotos);
    } catch { /* ignore */ }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > 50 * 1024 * 1024) { brandToast.slipped('That file is a little too large · keep it under 50MB'); return; }
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      brandToast.slipped('Only images and videos can be pinned to the wall'); return;
    }
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      await axios.post(`${API}/events/${event.event_id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      brandToast.gentle(file.type.startsWith('video/') ? 'Your reel is on the wall' : 'Your moment is on the wall');
      fetchPhotos();
    } catch (e) { brandToast.slipped('A moment slipped away · please try again'); }
    finally { setUploading(false); }
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleUpload(e.dataTransfer.files);
  };

  const downloadQR = async () => {
    if (!qrCardRef.current) return;
    try {
      const htmlToImage = await import('html-to-image');
      const dataUrl = await htmlToImage.toPng(qrCardRef.current, { quality: 1.0, pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `${event.event_name}-qr.png`;
      link.href = dataUrl;
      link.click();
      brandToast.gentle('The plate is saved');
    } catch (e) { brandToast.slipped("That couldn't be saved · please try again"); }
  };

  const downloadPhoto = async (photo) => {
    try {
      const res = await fetch(getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`));
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const ext = photo.content_type?.startsWith('video/')
        ? (photo.original_filename?.split('.').pop() || 'webm')
        : (photo.original_filename?.split('.').pop() || 'jpg');
      link.download = photo.original_filename || `event-media.${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
      brandToast.whisper('Saving to your photos…');
    } catch (e) { brandToast.slipped("That couldn't be saved · please try again"); }
  };

  const downloadZip = async () => {
    setDownloadingZip(true);
    try {
      const res = await fetch(`${API}/events/${event.event_id}/download-zip`);
      if (!res.ok) throw new Error('ZIP download failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${event.event_name.replace(/\s/g, '_')}_collection.zip`;
      link.click();
      brandToast.gentle('The collection is on its way');
    } catch (e) { brandToast.slipped("That collection couldn't be saved · please try again"); }
    finally { setDownloadingZip(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/event/${slug}`);
      setCopied(true);
      brandToast.whisper('The link is on the clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { brandToast.slipped("That link couldn't be copied · please try again"); }
  };

  const shareEvent = async () => {
    const url = `${window.location.origin}/event/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: event.event_name, url }); }
      catch (e) { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <PageShell testId="event-page-loading">
        <PageGutter>
          <div style={{
            padding: '120px 0', textAlign: 'center',
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 14, color: MODE_TWO.inkMuted,
          }}>Opening the night…</div>
        </PageGutter>
      </PageShell>
    );
  }
  if (!event) {
    return (
      <PageShell testId="event-page-not-found">
        <PageGutter>
          <GraphitePanel padding="48px 32px">
            <div style={{
              textAlign: 'center',
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 15, color: MODE_TWO.inkMuted,
            }}>That night couldn't be opened ✦</div>
          </GraphitePanel>
        </PageGutter>
      </PageShell>
    );
  }

  // Canonical guest URL — ONE doorway QR for the entire evening.
  // The share-plate QR (top of Setup) renders this; guests land on
  // the Guest Welcome ✦ flow, type their name, and step straight
  // into seating + entitled experiences. Legacy /event/:slug/guest
  // is intentionally retired as the public-facing canonical URL.
  const eventUrl = `${window.location.origin}/access/${slug}`;
  // iter183 — gallery filters now recognise lens notes (videos with
  // baked-in handwriting) and one-word messages. "Messages" folds
  // BOTH guestbook entries AND Drop One Word ✦ entries — they're
  // both text-shape contributions from the guest's POV. Videos
  // includes lens notes (they're still videos). Photos category
  // excludes everything that has a non-photo source.
  const filteredPhotos = galleryFilter === 'all' ? photos
    : galleryFilter === 'photos'     ? photos.filter(p => p.content_type?.startsWith('image/') && p.source !== 'guestbook' && p.source !== 'challenge' && p.source !== 'lens_note' && p.source !== 'one_word')
    : galleryFilter === 'videos'     ? photos.filter(p => (p.content_type?.startsWith('video/') || p.source === 'lens_note') && p.source !== 'guestbook' && p.source !== 'challenge')
    : galleryFilter === 'messages'   ? photos.filter(p => p.source === 'guestbook' || p.source === 'one_word')
    : galleryFilter === 'challenges' ? photos.filter(p => p.source === 'challenge')
    : photos;

  return (
    <PageShell testId="event-page-shell">
      <Year6TeamPicker event={event} />
      <PageGutter>
        <EventHeader
          event={event}
          photoCount={photos.length}
          onBack={() => navigate('/dashboard')}
        />

        <PaymentNote
          status={event.payment_status}
          paymentStatus={paymentStatus}
          celebrationShown={celebrationShown}
        />

        <TabBar tab={tab} setTab={(t) => {
          setTab(t);
          const next = new URLSearchParams(searchParams);
          next.set('tab', t);
          setSearchParams(next, { replace: true });
        }} tabs={visibleTabs} />

        <div data-testid={`event-tab-panel-${tab}`}>
          {tab === 'setup' && (
            <SetupTab
              event={event}
              slug={slug}
              eventUrl={eventUrl}
              copied={copied}
              copyLink={copyLink}
              shareEvent={shareEvent}
              qrCardRef={qrCardRef}
              savedQrDesign={savedQrDesign}
              setShowDesignEditor={setShowDesignEditor}
              downloadQR={downloadQR}
              fetchEvent={fetchEvent}
              onUpdate={fetchEvent}
              showKioskQr={showKioskQr}
              setShowKioskQr={setShowKioskQr}
              navigate={navigate}
              pairingFocusPulse={pairingFocusPulse}
            />
          )}
          {tab === 'wall' && (
            <WallTab
              event={event}
              photos={photos}
              filteredPhotos={filteredPhotos}
              galleryFilter={galleryFilter}
              setGalleryFilter={setGalleryFilter}
              uploading={uploading}
              dragActive={dragActive}
              handleUpload={handleUpload}
              handleDrag={handleDrag}
              handleDrop={handleDrop}
              fileInputRef={fileInputRef}
              setLightboxPhoto={setLightboxPhoto}
              downloadPhoto={downloadPhoto}
              downloadZip={downloadZip}
              downloadingZip={downloadingZip}
              newPhotoCount={newPhotoCount}
              navigate={navigate}
              slug={slug}
            />
          )}
          {tab === 'archive' && (
            <ArchiveTab
              event={event}
              slug={slug}
              navigate={navigate}
              setShowChaptersModal={setShowChaptersModal}
            />
          )}
          {tab === 'tonight' && tonightVisible && (
            <TonightTab event={event} />
          )}
          {tab === 'recap' && (
            <RecapTab
              event={event}
              photos={photos}
              slug={slug}
              navigate={navigate}
            />
          )}
        </div>
      </PageGutter>

      {/* Modals & overlays */}
      <Lightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} downloadPhoto={downloadPhoto} />
      {showPaywall && <PaywallModal event={event} onClose={() => setShowPaywall(false)} onUpgraded={fetchEvent} />}
      {showDesignEditor && (
        <QRDesignEditor
          eventUrl={eventUrl}
          eventName={event?.event_name}
          onClose={() => setShowDesignEditor(false)}
          onSave={handleSaveQrDesign}
          savedDesign={savedQrDesign}
        />
      )}
      <KioskQrModal
        open={showKioskQr}
        onClose={() => setShowKioskQr(false)}
        pocketBoothUrl={`${window.location.origin}/event/${slug}/pocket-booth`}
        eventName={event?.event_name}
      />
      {showChaptersModal && (
        <MemoryChaptersModal
          event={event}
          onClose={() => setShowChaptersModal(false)}
        />
      )}

      <style>{`
        @keyframes mode2Breathe {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
      `}</style>
    </PageShell>
  );
};

export default EventPage;
